/** EFFET : lecture d'un ralenti sur un canvas (images JPEG ou maillage dessiné). */
import { drawMesh } from '../ar/meshRenderer';
import { makeProjector } from '../ar/projection';
import { decodeMeshFrame } from '../mesh/codec';
import type { Clip } from './clipCodec';
import { frameAt, playbackDuration } from './playback';

const WIDTH = 320;
const HEIGHT = 240;
const PAUSE_MS = 700;

export interface ReplayPlayer {
  readonly element: HTMLCanvasElement;
  stop(): void;
}

export function createReplayPlayer(clip: Clip, speed = 0.5): ReplayPlayer {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  canvas.className = 'replay';
  canvas.dataset['testid'] = 'replay';
  canvas.dataset['frames'] = String(clip.frames.length);
  canvas.dataset['kind'] = clip.kind;
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', 'Ralenti du craquage');
  const ctx = canvas.getContext('2d');
  let raf = 0;
  let stopped = false;

  // Images décodées une fois pour toutes (JPEG) ou points du maillage.
  const images: (ImageBitmap | null)[] = clip.frames.map(() => null);
  if (clip.kind === 'video') {
    clip.frames.forEach((f, i) => {
      void createImageBitmap(new Blob([f.data.slice()], { type: 'image/jpeg' }))
        .then((bmp) => {
          images[i] = bmp;
        })
        .catch(() => undefined);
    });
  }
  const meshes =
    clip.kind === 'mesh' ? clip.frames.map((f) => decodeMeshFrame(f.data.slice().buffer)) : [];
  const project = makeProjector({
    width: WIDTH,
    height: HEIGHT,
    sourceAspect: 4 / 3,
    fit: 'contain',
    mirror: true,
  });

  const cycle = playbackDuration(clip.frames, speed) + PAUSE_MS;
  const start = performance.now();
  let drawn = -1;
  const draw = (): void => {
    if (stopped || !ctx) return;
    raf = requestAnimationFrame(draw);
    const elapsed = cycle > PAUSE_MS ? (performance.now() - start) % cycle : 0;
    const i = frameAt(clip.frames, elapsed, speed);
    canvas.dataset['frame'] = String(i);
    if (i === drawn) return; // même image : rien à redessiner
    if (clip.kind === 'video') {
      const img = images[i];
      if (!img) return; // pas encore décodée : on réessaie à l'image suivante
      ctx.drawImage(img, 0, 0, WIDTH, HEIGHT);
    } else {
      drawMesh(ctx, meshes[i]?.points ?? [], project, { width: WIDTH, height: HEIGHT });
    }
    drawn = i;
  };
  raf = requestAnimationFrame(draw);

  return {
    element: canvas,
    stop() {
      stopped = true;
      cancelAnimationFrame(raf);
      for (const img of images) img?.close();
    },
  };
}
